import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { V1Service } from '@zelos/codegen/apis';
import { classificationMeta } from '@zelos/shared/constants/meta';
import { current, getFormattedDate } from '@zelos/shared/lib/date-util';
import { useCommQueryData } from '@zelos/shared/lib/use-comm-query-data';
import produce from 'immer';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { v4 as uuidv4 } from 'uuid';
import {
  BannerBoardChipsWrapDiv,
  BannerBoardContentChipWrapper,
  BannerBoardContentDiv,
  BannerBoardContentsBottomDiv,
  BannerBoardContentSpan,
  BannerBoardContentsWrapDiv,
  BannerBoardContentTitleSpan,
  BannerBoardDivider,
  BannerBoardFilterDiv,
  BannerBoardListWrapDiv,
  BannerBoardLogo,
  BannerBoardTopSubTitleDiv,
  BannerBoardTopTitleDiv,
  BannerBoardWrapArticle,
} from '../banner-board-components';
import Chip from '@/components/comm/Chip';
import CustomSelect from '@/components/comm/CustomSelect';
import DdayChip from '@/components/comm/DdayChip';
import NewCircle from '@/components/comm/NewCircle';
import PaginatedPagination from '@/components/comm/PaginatedPagination';
import { colors } from '@/constants/colors';
import { testIds } from '@/constants/test-ids';
import { usePaginatedPagination } from '@/lib/use-pagination';

type JobListProps = {
  page?: number | null,
};

type JobsListRequest = {
  classificationId?: number,
  order?:
  ('-old' | '-registration_end_datetime' | 'old' | 'registration_end_datetime')[],
  page?: number,
  pageSize?: number,
  skillLevel?: number,
};

const defaultPageSize = 10;

const SelectStyled = styled(CustomSelect)`
width: 100px;
`;

const JobList = ({ page: pageProp }: JobListProps) => {
  const [jobsListRequest, setJobsListRequest] = useState<JobsListRequest>({
    pageSize: defaultPageSize,
    page: 1,
  });
  const router = useRouter();

  const { data: jobsList, isFetching } = useQuery(
    ['v1JobsList', jobsListRequest],
    () => V1Service.v1JobsList(jobsListRequest),
    { refetchOnWindowFocus: false },
  );

  const {
    totalPages,
    page,
    displayItems: displayJobList,
  } = usePaginatedPagination(jobsList);

  const { classificationList, findClassification, findLanguageNames } =
    useCommQueryData();

  const handlePageChange = (newPage: number) => {
    if (!jobsListRequest || jobsListRequest.page === newPage) return;
    router.push({ query: { page: newPage } });
  };

  const selectedClassificationMeta = useMemo(() => {
    const classificationId = jobsListRequest.classificationId;
    if (!classificationId) return;
    const classification = classificationList?.find(
      ({ id }) => classificationId === id,
    );
    if (!classification) return;
    return classificationMeta[classification.code];
  }, [classificationList, jobsListRequest.classificationId]);

  useEffect(() => {
    setJobsListRequest(produce((draft) => {
      draft.page = pageProp ?? 1;
    }));
  }, [pageProp]);

  return (
    <>
      <BannerBoardFilterDiv>
        <SelectStyled
          defaultValue="default"
          onChange={({ target: { value } }) =>
            setJobsListRequest(produce((draft) => {
              const selectedValue = Number(value);
              selectedValue && !Number.isNaN(selectedValue) ? (
                draft.classificationId = selectedValue
              ) : (
                delete draft.classificationId
              );
              delete draft.skillLevel;
            }))
          }
        >
          <option value="default" hidden>시험 구분</option>
          <option value="">전체</option>
          {classificationList?.map(({ id, code }) => (
            <option key={id} value={id}>
              {code}
            </option>
          ))}
        </SelectStyled>
        <SelectStyled
          defaultValue="default"
          onChange={({ target: { value } }) =>
            setJobsListRequest(produce((draft) => {
              const selectedValue = Number(value);
              selectedValue && !Number.isNaN(selectedValue) ? (
                draft.skillLevel = selectedValue
              ) : (
                delete draft.skillLevel
              );
            }))
          }
          disabled={!selectedClassificationMeta}
        >
          <option value="default" hidden>레벨</option>
          <option value="">전체</option>
          {selectedClassificationMeta?.levels.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </SelectStyled>
        <SelectStyled
          defaultValue="default"
          onChange={({ target: { value } }) => {
            const order: ('old' | 'registration_end_datetime')[] = [];
            if (value === 'registration_end_datetime' || value === 'old') {
              order.push(value);
            }
            setJobsListRequest(produce((draft) => {
              draft.order = order;
            }));
          }}
        >
          <option value="default" hidden>정렬순서</option>
          <option value="">기본</option>
          <option value="registration_end_datetime">마감순</option>
          <option value="old">등록순</option>
        </SelectStyled>
      </BannerBoardFilterDiv>
      <BannerBoardListWrapDiv>
        {!isFetching ? displayJobList.map(
          ({
            id,
            logo,
            position,
            companyName,
            career,
            careerMin,
            careerMax,
            demandEssentialSkill,
            essentialSkillClassification,
            essentialSkillLanguages,
            essentialSkillLevel,
            demandOptionalSkill,
            optionalSkillClassification,
            optionalSkillLanguages,
            optionalSkillLevel,
            demandOtherSkill,
            otherSkill,
            registrationEndDatetime,
            created,
          }) => {
            const essentialSkillClassificationCode = findClassification(essentialSkillClassification)?.code || null;

            const essentialSkillLanguageNames = findLanguageNames(essentialSkillLanguages) || null;

            const optionalSkillClassificationCode = findClassification(optionalSkillClassification)?.code || null;

            const optionalSkillLanguageNames = findLanguageNames(optionalSkillLanguages) || null;

            return (
              <Link
                key={id}
                href={{ pathname: '/jobs', query: { page, id } }}
                data-testid={testIds.jobs.JobsList.Item}
              >
                <BannerBoardWrapArticle minHeight="300px">
                  <BannerBoardLogo src={logo.url} alt="logo" />
                  <BannerBoardContentsWrapDiv>
                    <BannerBoardTopTitleDiv>
                      <span>{current().diff(created, 'day') <= 7 && <><NewCircle />&nbsp;</>}{position}</span>
                    </BannerBoardTopTitleDiv>
                    <BannerBoardTopSubTitleDiv>
                      {companyName}
                    </BannerBoardTopSubTitleDiv>
                    <BannerBoardChipsWrapDiv>
                      {demandEssentialSkill && (
                        <Chip
                          txt="필수"
                          backgroundColor={colors.blue}
                          textColor={colors.whiteGrey3}
                          size="small"
                          fontSize="12px"
                        />
                      )}
                      {demandOptionalSkill && (
                        <Chip
                          txt="우대"
                          backgroundColor={colors.textPrimary}
                          textColor={colors.white}
                          size="small"
                          fontSize="12px"
                        />
                      )}
                      {demandOtherSkill && (
                        <Chip
                          txt="기타"
                          backgroundColor={colors.grey5}
                          textColor={colors.indigo}
                          size="small"
                          fontSize="12px"
                        />
                      )}
                    </BannerBoardChipsWrapDiv>
                  </BannerBoardContentsWrapDiv>
                  <BannerBoardDivider />
                  <BannerBoardContentsBottomDiv>
                    {career && (
                      <BannerBoardContentDiv>
                        <BannerBoardContentTitleSpan>
                        모집 구분
                        </BannerBoardContentTitleSpan>
                        {career === 'JR' && (
                          <BannerBoardContentSpan>
                                신입
                          </BannerBoardContentSpan>
                        )}
                        {career === 'SR' && (
                          <BannerBoardContentSpan>
                            {careerMin}년 ~ {careerMax}년
                          </BannerBoardContentSpan>
                        )}
                      </BannerBoardContentDiv>
                    )}
                    {demandEssentialSkill && (
                      <BannerBoardContentDiv>
                        <BannerBoardContentTitleSpan>
                              필수
                        </BannerBoardContentTitleSpan>
                        <BannerBoardContentSpan>
                          {essentialSkillClassificationCode && <span>{essentialSkillClassificationCode}</span>}
                          {essentialSkillLanguageNames?.map((language, index) => <span key={uuidv4()}>{language}{index < essentialSkillLanguageNames.length - 1 && ','}</span>)}
                          {(essentialSkillLanguageNames?.length || 0) > 1 && <span>중 택 1</span>}
                          {essentialSkillLevel && <span>Lv{essentialSkillLevel} 이상</span>}
                        </BannerBoardContentSpan>
                      </BannerBoardContentDiv>
                    )}
                    {demandOptionalSkill && (
                      <BannerBoardContentDiv>
                        <BannerBoardContentTitleSpan>
                              우대
                        </BannerBoardContentTitleSpan>
                        <BannerBoardContentSpan>
                          {optionalSkillClassificationCode && <span>{optionalSkillClassificationCode}</span>}
                          {optionalSkillLanguageNames?.map((language, index) => <span key={uuidv4()}>{language}{index < optionalSkillLanguageNames.length - 1 && ','}</span>)}
                          {(optionalSkillLanguageNames?.length || 0) > 1 && <span>중 택 1</span>}
                          {optionalSkillLevel && <span>Lv{optionalSkillLevel} 이상</span>}
                        </BannerBoardContentSpan>
                      </BannerBoardContentDiv>
                    )}
                    {demandOtherSkill && (
                      <BannerBoardContentDiv>
                        <BannerBoardContentTitleSpan>
                              기타
                        </BannerBoardContentTitleSpan>
                        <BannerBoardContentSpan>
                          {otherSkill}
                        </BannerBoardContentSpan>
                      </BannerBoardContentDiv>
                    )}
                    <BannerBoardContentDiv center>
                      <BannerBoardContentTitleSpan>
                      지원 마감
                      </BannerBoardContentTitleSpan>
                      {registrationEndDatetime ? (
                        <BannerBoardContentChipWrapper>
                          <BannerBoardContentSpan>
                            {getFormattedDate(
                              registrationEndDatetime,
                              'YYYY-MM-DD(dd) HH:mm',
                            )}
                          </BannerBoardContentSpan>
                          <DdayChip date={registrationEndDatetime} />
                        </BannerBoardContentChipWrapper>
                      ) : <BannerBoardContentSpan>상시모집</BannerBoardContentSpan>}
                    </BannerBoardContentDiv>
                  </BannerBoardContentsBottomDiv>
                </BannerBoardWrapArticle>
              </Link>
            );
          },
        ) : (
          <>
            {Array.from({ length: 4 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <BannerBoardWrapArticle minHeight="300px" key={index}>
                <Skeleton width={72} height={72} />
                <BannerBoardContentsWrapDiv>
                  <BannerBoardTopTitleDiv>
                    <Skeleton width={120} />
                  </BannerBoardTopTitleDiv>
                  <BannerBoardTopSubTitleDiv>
                    <Skeleton width={80} />
                  </BannerBoardTopSubTitleDiv>
                  <BannerBoardChipsWrapDiv>
                    <Skeleton width={80} />
                  </BannerBoardChipsWrapDiv>
                </BannerBoardContentsWrapDiv>
                <BannerBoardDivider />
                <BannerBoardContentsBottomDiv>
                  <Skeleton count={5} width="70%" />
                </BannerBoardContentsBottomDiv>
              </BannerBoardWrapArticle>
            ))}
          </>
        )}
      </BannerBoardListWrapDiv>
      <PaginatedPagination
        pageCount={totalPages}
        currentPage={page}
        onChange={handlePageChange}
      />
    </>
  );
};

export default JobList;
